import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Form, Input } from 'antd';
import { useState } from 'react';
import { useAppDispatch } from '@/app/store/hooks';
import { phoneLogin, requestLoginCaptcha } from '@/features/auth/api/authApi';
import { loginSucceeded } from '@/entities/session/model/sessionSlice';
import { storeAuthToken } from '@/entities/session/model/authStorage';
import { AgreementField } from '@/features/auth/ui/AgreementField';
import { useCaptchaCountdown } from '@/features/auth/model/useCaptchaCountdown';
import { normalizeApiError } from '@/shared/api/errors';
import { suppressApiErrorToast } from '@/shared/api/httpClient';
/** 手机号格式，与后端 DTO 校验保持一致。 */
const PHONE_PATTERN = /^1[3-9]\d{9}$/;
/** 渲染验证码登录表单，登录成功后写入令牌并同步全局会话状态。 */
export function SmsLoginForm(props) {
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const { countdown, startCountdown } = useCaptchaCountdown();
    const [errorText, setErrorText] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [sendingCaptcha, setSendingCaptcha] = useState(false);
    /** 校验手机号后请求发送验证码，并进入 60 秒冷却。 */
    async function handleSendCaptcha() {
        try {
            await form.validateFields(['phone']);
        }
        catch {
            return;
        }
        setSendingCaptcha(true);
        setErrorText(null);
        try {
            await requestLoginCaptcha(form.getFieldValue('phone').trim());
            startCountdown();
        }
        catch (error) {
            /** 失败文案就地展示在表单里，同步抑制统一出口，避免同一次失败弹两处。 */
            setErrorText(normalizeApiError(error).message);
            suppressApiErrorToast(error);
        }
        finally {
            setSendingCaptcha(false);
        }
    }
    /** 提交验证码登录，成功后保存令牌并更新会话状态。 */
    async function handleFinish(values) {
        setSubmitting(true);
        setErrorText(null);
        try {
            const result = await phoneLogin({ phone: values.phone.trim(), captcha: values.captcha });
            storeAuthToken(result.token);
            dispatch(loginSucceeded(result.user));
        }
        catch (error) {
            /** 失败文案就地展示在表单里，同步抑制统一出口，避免同一次失败弹两处。 */
            setErrorText(normalizeApiError(error).message);
            suppressApiErrorToast(error);
        }
        finally {
            setSubmitting(false);
        }
    }
    return (_jsxs(Form, { className: "auth-form", form: form, layout: "vertical", requiredMark: false, onFinish: (values) => {
            void handleFinish(values);
        }, children: [errorText && (_jsx("p", { className: "auth-form__error", role: "alert", children: errorText })), _jsx(Form.Item, { label: "\u624B\u673A\u53F7", name: "phone", rules: [
                    { required: true, message: '请输入手机号' },
                    { pattern: PHONE_PATTERN, message: '手机号格式不正确' },
                ], children: _jsx(Input, { inputMode: "numeric", maxLength: 11, placeholder: "\u8BF7\u8F93\u5165\u624B\u673A\u53F7", autoComplete: "tel" }) }), _jsxs("div", { className: "auth-form__captcha-row", children: [_jsx(Form.Item, { className: "auth-form__captcha-field", label: "\u9A8C\u8BC1\u7801", name: "captcha", rules: [
                            { required: true, message: '请输入验证码' },
                            { pattern: /^\d{6}$/, message: '验证码为 6 位数字' },
                        ], children: _jsx(Input, { inputMode: "numeric", maxLength: 6, placeholder: "6 \u4F4D\u9A8C\u8BC1\u7801", autoComplete: "one-time-code" }) }), _jsx(Button, { className: "auth-form__captcha-button", type: "default", disabled: countdown > 0, loading: sendingCaptcha, onClick: () => {
                            void handleSendCaptcha();
                        }, children: countdown > 0 ? `${String(countdown)}s 后重发` : '获取验证码' })] }), _jsx(AgreementField, { accepted: props.agreementAccepted, onAcceptedChange: props.onAgreementChange }), _jsx(Button, { className: "auth-form__submit", type: "primary", htmlType: "submit", block: true, disabled: !props.agreementAccepted, loading: submitting, children: "\u767B\u5F55 / \u6CE8\u518C" })] }));
}
